import {
  ChemistryPreferences,
  DEFAULT_MIN_SIMILARITY_CUTOFF,
  EMPTY_MOLFILE,
} from 'chemistry/ChemistryConstants';
import type {
  ChemSearchSubmit,
  MoleculeChemSearchSubmit,
  ReactionChemSearchSubmit,
} from 'chemistry/chemSearchSubmit';
import {ChemicalStructureDrawerValue} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {ListingRendererType, ListingTypes} from 'nav/expanded/ListingRendererConstants';
import preferences from 'Preferences';
import * as FilterUtil from 'search/FilterUtil';
import {
  ChemicalStructureFilter,
  ChemicalStructureFilterFormat,
  ChemicalStructureSearchOperator,
  FilterKey,
  isChemicalStructureFilter,
  MultiValueFilter,
  Operator,
  SearchParams,
  SearchResults,
} from 'search/SearchConstants';
import {mergeFiltersToSearchParams} from 'search/SearchUtil';
import APIUtil from 'util/APIUtil';

import {ChemSearchFolderItemListingExtras} from './ChemistryConstants';

interface ChemicalStructureClauseForFilter {
  chemicalStructureQuery: string;
  minSimilarity?: number;
  reactionComponentRole?: string | null;
}

/**
 * Get the unique mol query from the search params, if it exists, otherwise null.
 */
export function extractMolQuery(searchParams: SearchParams): string | null {
  for (const filter of searchParams.filters) {
    if (isChemicalStructureFilter(filter) && filter.format === ChemicalStructureFilterFormat.MOL) {
      return filter.value;
    }
  }
  return null;
}

function extractSubstructureMolQuery(searchParams: SearchParams): string | null {
  for (const filter of searchParams.filters) {
    if (
      isChemicalStructureFilter(filter) &&
      filter.format === ChemicalStructureFilterFormat.MOL &&
      filter.operator === Operator.SUBSTRUCTURE
    ) {
      return filter.value;
    }
  }
  return null;
}

export function isEmptyChemicalStructureQuery(chemicalStructureQuery: ChemicalStructureDrawerValue): boolean {
  return chemicalStructureQuery == null || chemicalStructureQuery === EMPTY_MOLFILE;
}

export function isNonEmptyChemicalStructureQuery(
  chemicalStructureQuery: ChemicalStructureDrawerValue
): chemicalStructureQuery is string {
  return !isEmptyChemicalStructureQuery(chemicalStructureQuery);
}

export function getNonEmptyChemicalStructureQuery(
  chemicalStructureQuery: ChemicalStructureDrawerValue
): string {
  if (!isNonEmptyChemicalStructureQuery(chemicalStructureQuery)) {
    throw new Error('Expected non-empty chemical structure query');
  }
  return chemicalStructureQuery;
}

export function isReactionChemSearch(searchParams: SearchParams): boolean {
  const existingTypesFilter = FilterUtil.getFilterByKey(
    searchParams.filters,
    FilterKey.TYPES
  ) as MultiValueFilter | null;
  return (
    existingTypesFilter?.value.length === 1 && existingTypesFilter.value[0] === ModelTypeConstants.REACTION
  );
}

function chemicalStructureFiltersFromClauses(
  clauses: Array<ChemicalStructureClauseForFilter>,
  chemSearchOperator: ChemicalStructureSearchOperator,
  isReactionSearch: boolean
): Array<ChemicalStructureFilter> {
  return clauses.map((clause) => {
    const chemicalStructureFilter: ChemicalStructureFilter = {
      key: FilterKey.CHEMICAL_STRUCTURE,
      operator: chemSearchOperator,
      format: ChemicalStructureFilterFormat.MOL,
      value: clause.chemicalStructureQuery,
      minSimilarity: chemSearchOperator === Operator.SIMILARITY ? clause.minSimilarity : undefined,
    };
    if (isReactionSearch) {
      chemicalStructureFilter.reactionComponentRole = clause.reactionComponentRole ?? null;
    }
    return chemicalStructureFilter;
  });
}

function mergeChemSearchFilters(
  searchParams: SearchParams,
  chemSearchOperator: ChemicalStructureSearchOperator,
  clauses: Array<ChemicalStructureClauseForFilter>,
  isReactionSearch: boolean
): SearchParams {
  const typeFilter: MultiValueFilter = {
    key: FilterKey.TYPES,
    operator: Operator.IS_ONE_OF,
    value: isReactionSearch ? [ModelTypeConstants.REACTION] : [ModelTypeConstants.MOLECULE],
  };
  const chemicalStructureFilters = chemicalStructureFiltersFromClauses(
    clauses,
    chemSearchOperator,
    isReactionSearch
  );
  return mergeFiltersToSearchParams(searchParams, [typeFilter, ...chemicalStructureFilters]);
}

export function updateMoleculeChemSearchParams(
  searchParams: SearchParams,
  submit: MoleculeChemSearchSubmit
): SearchParams {
  return mergeChemSearchFilters(
    searchParams,
    submit.chemSearchOperator,
    [
      {
        chemicalStructureQuery: submit.chemicalStructureQuery,
        minSimilarity: submit.minSimilarity,
      },
    ],
    false
  );
}

export function updateReactionChemSearchParams(
  searchParams: SearchParams,
  submit: ReactionChemSearchSubmit
): SearchParams {
  return mergeChemSearchFilters(
    searchParams,
    submit.chemSearchOperator,
    submit.clauses.map((clause) => ({
      chemicalStructureQuery: clause.chemicalStructureQuery,
      minSimilarity: clause.minSimilarity,
      reactionComponentRole: clause.reactionComponentRole,
    })),
    true
  );
}

export function applyChemSearchSubmit(searchParams: SearchParams, submit: ChemSearchSubmit): SearchParams {
  if (submit.mode === 'molecule') {
    return updateMoleculeChemSearchParams(searchParams, submit);
  }
  return updateReactionChemSearchParams(searchParams, submit);
}

export interface ToggleHighlightParams {
  showToggleHighlightOption: boolean;
  shouldHighlightSubstructure: boolean;
  setShouldHighlightSubstructure: (shouldHighlightSubstructure: boolean) => void;
}

export function getChemSearchOperator(searchParams: SearchParams): Operator | null {
  for (const filter of searchParams.filters) {
    if (isChemicalStructureFilter(filter)) {
      return filter.operator;
    }
  }
  return null;
}

export function isSubstructureSearch(searchParams: SearchParams): boolean {
  return getChemSearchOperator(searchParams) === Operator.SUBSTRUCTURE;
}

export function shouldShowChemSearchColumnsFirst(
  items: Array<ListingTypes[ListingRendererType.FOLDER_ITEM]>
): boolean {
  return items.every((item) => APIUtil.isMolecule(item.id));
}

export function isSimilaritySearch(searchParams: SearchParams): boolean {
  return getChemSearchOperator(searchParams) === Operator.SIMILARITY;
}

export function isChemSearch(searchParams: SearchParams): boolean {
  return isSubstructureSearch(searchParams) || isSimilaritySearch(searchParams);
}

export function getChemSearchFolderItemListingExtras(
  searchParams: SearchParams,
  shouldHighlightSubstructure: boolean,
  searchResults: SearchResults | null
): ChemSearchFolderItemListingExtras | null {
  return isChemSearch(searchParams)
    ? {
        substructureQuery: extractSubstructureMolQuery(searchParams),
        shouldHighlightSubstructure,
        searchScoresById: searchResults?.scoresById,
      }
    : null;
}

export function getSimilarityOptionLabel(minSimilarity: number): string {
  return `>=${minSimilarity * 100}%`;
}

export function getDefaultMinSimilarity(searchParams: SearchParams): number {
  const existingSimilarityFilter = searchParams.filters.find(
    (filter): filter is ChemicalStructureFilter =>
      isChemicalStructureFilter(filter) && filter.operator === Operator.SIMILARITY
  );
  return (
    existingSimilarityFilter?.minSimilarity ??
    preferences.get(ChemistryPreferences.MIN_SIMILARITY) ??
    DEFAULT_MIN_SIMILARITY_CUTOFF
  );
}

export function getDefaultChemSearchOperator(searchParams: SearchParams): ChemicalStructureSearchOperator {
  if (isSimilaritySearch(searchParams)) {
    return Operator.SIMILARITY;
  } else if (isSubstructureSearch(searchParams)) {
    return Operator.SUBSTRUCTURE;
  } else {
    return preferences.get(ChemistryPreferences.CHEM_SEARCH_TYPE) ?? Operator.SUBSTRUCTURE;
  }
}

export function getChemSearchChipLabel(searchParams: SearchParams): string {
  let chipText = '';
  if (isSubstructureSearch(searchParams)) {
    chipText = 'Substructure Search';
  }
  if (isSimilaritySearch(searchParams)) {
    chipText = 'Similarity Search';
  }
  return chipText;
}
